import {
  AnimatePresence,
  isValidMotionProp,
  motion,
  useWillChange,
} from "framer-motion";

import { appName } from "../../config";
import { popUpVariant } from "../../animateConstant";
import { useEffect, useMemo, useRef, useState } from "react";
import { getFixedStyle } from "../../hook/useDeviceType";

import { useTranslation } from "react-i18next";
import { IoCloseOutline } from "react-icons/io5";
import { useNavigate } from "react-router";
import {
  AbsoluteCenter,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  IconButton,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  chakra,
  shouldForwardProp,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiShareForwardFill } from "react-icons/ri";
import { COMMON_WEB_PATH } from "../../constant";
import useDevice from "../../hook/useDevice";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const StepDivider = ({ nextStep = false }) => {
  return (
    <Box position="relative" my="15px" w="100%">
      <Divider />
      <AbsoluteCenter
        bg="brand.500"
        px="20px"
        borderRadius={"20px"}
        fontWeight={"bold"}
      >
        {nextStep ? "下一步" : "第一步"}
      </AbsoluteCenter>
    </Box>
  );
};

const DownloadApp = () => {
  const { t } = useTranslation();
  const i18n = (key, props) => t(`app.${key}`, { ...props });
  const i18n_common = (key) => t(`common.${key}`);

  const qrRef = useRef(null);
  const [imgSrc, setImgSrc] = useState("");

  const { deviceType, deviceVersion } = useDevice();

  const iOSVersionHigherThan17 = useMemo(() => {
    if (deviceType === "iOS") {
      return Number(deviceVersion[0]) >= 17;
    }
  }, [deviceVersion, deviceType]);

  const openLinkFromDevice = useMemo(() => {
    if (deviceType == "Android") {
      return `${process.env.REACT_APP_API_URL?.replace(
        "https",
        "googlechrome"
      )}`;
    } else {
      return iOSVersionHigherThan17
        ? `x-safari-${process.env.REACT_APP_API_URL}`
        : process.env.REACT_APP_API_URL;
    }
  }, [deviceType, iOSVersionHigherThan17]);

  const willChange = useWillChange();

  const { colorMode, toggleColorMode } = useColorMode();
  const boxBg = useColorModeValue("light.200", "dark.200");
  const boxLightBg = useColorModeValue("light.100", "dark.100");
  const textColor = useColorModeValue("dark.100", "light.100");

  const [isClosing, setIsClosing] = useState(false);

  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {!isClosing && (
        <Box
          zIndex={9999}
          backdropBlur="md"
          position="fixed"
          top="0px"
          left="0px"
          w="100%"
          h="100%"
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <ChakraBox
            variants={popUpVariant}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ willChange }}
            transition={{
              duration: 0.2,
            }}
            w="100%"
            h="100%"
            overflowY="scroll"
            pb={"25px"}
            pt={"10px"}
            px={"15px"}
            borderRadius={"lg"}
            maxW={"450px"}
            bg={boxBg}
            color={textColor}
          >
            <Flex
              alignItems={"center"}
              justifyContent={"flex-end"}
              color={textColor}
              fontSize="3xl"
            >
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
                fontSize="3xl"
                size="sm"
                icon={<IoCloseOutline />}
              />
            </Flex>
            <Text fontSize="2xl" fontWeight={"bold"} textAlign={"center"}>
              {i18n("建立捷徑教學")}
            </Text>
            <Divider mt="10px" mb="20px" />
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>iOS</Tab>
                <Tab>Android</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Flex
                    w="100%"
                    direction={"column"}
                    gap="15px"
                    alignItems={"center"}
                  >
                    <Text textAlign={"center"}>
                      {i18n(
                        "為了讓您有更好的使用者體驗，建議您將iOS版本升級為17.0.1以上。"
                      )}
                    </Text>

                    <StepDivider />

                    <Text textAlign={"center"}>
                      {i18n("請使用您iPhone手機的Safari瀏覽器開啟")}
                    </Text>
                    <Button
                      as="a"
                      href={openLinkFromDevice}
                      target="_blank"
                      type="button"
                      leftIcon={<RiShareForwardFill />}
                    >
                      {i18n("前往網頁開始設定")}
                    </Button>

                    <StepDivider nextStep />

                    <Text textAlign={"center"}>
                      {i18n("前往該頁面後， 點選下方工具列的『分享』按鈕")}
                    </Text>

                    <Image
                      src={`${COMMON_WEB_PATH}/911TW/PWA/iOS_step_1_3.jpg?14`}
                    />

                    <StepDivider nextStep />

                    <Text textAlign={"center"}>
                      {i18n("點選『加入主畫面』，就完成囉！")}
                    </Text>

                    <Image
                      src={`${COMMON_WEB_PATH}/911TW/PWA/iOS_step_2_3.jpg?14`}
                    />

                    <StepDivider nextStep />

                    <Text textAlign={"center"}>
                      {i18n(
                        "完成安裝登入後，記得訂閱通知，才不會錯過推播的專屬獎勵唷！"
                      )}
                    </Text>

                    <Image
                      src={`${COMMON_WEB_PATH}/911TW/PWA/iOS_step_3_3.jpg`}
                    />
                  </Flex>
                </TabPanel>
                <TabPanel>
                  <Flex
                    w="100%"
                    direction={"column"}
                    gap="15px"
                    alignItems={"center"}
                  >
                    <StepDivider />

                    <Text textAlign={"center"}>
                      {i18n("請使用您的手機的Google瀏覽器開啟")}
                    </Text>
                    <Button
                      as="a"
                      href={openLinkFromDevice}
                      target="_blank"
                      type="button"
                      leftIcon={<RiShareForwardFill />}
                    >
                      {i18n("前往網頁開始設定")}
                    </Button>

                    <StepDivider nextStep />

                    <Text textAlign={"center"}>
                      {i18n("按一下瀏覽器右上角「更多」圖示按鈕")}
                    </Text>
                    <Image
                      src={`${COMMON_WEB_PATH}/911TW/PWA/Android_step_1_3.jpg?14`}
                    />

                    <StepDivider nextStep />

                    <Text textAlign={"center"}>
                      {i18n("點選「安裝應用程式」，就完成囉！")}
                    </Text>

                    <Image
                      src={`${COMMON_WEB_PATH}/911TW/PWA/Android_step_2_3.jpg?14`}
                    />
                  </Flex>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ChakraBox>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default DownloadApp;
